import {
  Paper,
  Box,
  Checkbox,
  useTheme,
  Typography,
} from "@mui/material";
import React from "react";
import LocalStorageUtil from "../../utils/localStorageUtil";
import { storageKeys } from "../../utils/constants";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useResponsive from "../../hooks/useResponsive";
import apiService from "../../apiService/ApiService";
import { useAlert } from "../../contexts/AlertContext";
import { ObsTaskExecuteApi } from "../../utils/urlConst";
import ConfirmationAlert from "../ConfirmationAlert";

interface MaterialCardFieldProps {
  initialExecuted?: boolean;
}

const MaterialCardField: React.FC<MaterialCardFieldProps> = ({ initialExecuted = false }) => {
  const taskId = LocalStorageUtil.getItem(storageKeys.taskId, "");
  const userId = LocalStorageUtil.getItem(storageKeys.userId, "");
  const workerId = LocalStorageUtil.getItem(storageKeys.workerId, "");
  const isFlexRole = LocalStorageUtil.getItem(storageKeys.isFlexRole, 0);
  const theme = useTheme();
  const { isDesktop } = useResponsive();
  const { showAlert } = useAlert();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isChecked, setIsChecked] = React.useState(initialExecuted);
  const [isDisabled, setIsDisabled] = React.useState(initialExecuted);
  const [showConfirmation, setShowConfirmation] = React.useState(false);

  const handleCheckboxChange = () => {
    if (!taskId || isDisabled) return;
    setShowConfirmation(true);
  };

  const handleConfirmExecution = async () => {
    try {
      setIsDisabled(true);
      
      const requestData = JSON.stringify({
        taskId: taskId,
        userId: userId,
        workerId: workerId,
        isFlexRole: isFlexRole,
      });
      
    const response: any = await apiService.post(ObsTaskExecuteApi, requestData);
      
      if (response && response.status === true) {
        setIsChecked(true);
        showAlert(t("observation.obsTaskExecuted") || "Observation task executed successfully", "success");
        setTimeout(() => {
          navigate(-1);
        }, 500);
        // Keep disabled after successful execution
      } else {
          setIsDisabled(false);
          setIsChecked(false);
          showAlert(t("common.server_error") || "Failed to mark task as executed", "error");
        }
      } catch (error) {
        setIsDisabled(false);
        setIsChecked(false);
        console.error('Error marking task as executed:', error);
        showAlert(t("common.server_error") || "Error marking task as executed", "error");
      }
  };

  const handleCloseConfirmation = () => {
    setShowConfirmation(false);
  };

  return (
    <Paper
      elevation={1}
      sx={{
        p: 1,
        mt: "25px",
        width: isDesktop ? "90%" : "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
          <Typography
            fontFamily={"Nunito Sans Regular700"}
            sx={{
              fontSize: "18px",
              color: theme.palette.primary.main,
            }}
          >
            Waarneming
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            ml: 2,
          }}
        >
          <Typography
            fontFamily="Nunito Sans Regular500"
            sx={{ color: "primary.main", fontSize: "14px" }}
          >
            Uitgevoerd
          </Typography>

          <Checkbox
            checked={isChecked}
            onChange={handleCheckboxChange}
            sx={{
              color: "primary.main",
              "&.Mui-checked": {
                color: "primary.main",
              },
            }}
            disabled={isDisabled}
          />
        </Box>
      </Box>

      <ConfirmationAlert
        open={showConfirmation}
        message={"Weet u zeker dat u de taak wilt voltooien?"}
        onDelete={handleConfirmExecution}
        handleClose={handleCloseConfirmation}
        yesLabel={"Ja"}
        noLabel={"Nee"}
      />
    </Paper>
  );
};

export { MaterialCardField };
